'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import clsx from 'clsx';
import { useOnViewport } from '@/components/core/hooks';
import configuration from '@/configuration';
import { UIComponent } from '../..';
import { useAds } from './context';
import * as styles from './ads.styles';
import { Element } from '../../atoms';

type Size = {
  width: number;
  height: number;
};

type AdEvent = CustomEvent<{
  isEmpty: boolean;
  size?: Size;
  isVideo?: boolean;
  image?: string;
}>;

export type AdsStateEnum = 'hidden' | 'loading' | 'loaded' | 'empty';

type statusChangeEventParam = {
  status: AdsStateEnum;
};

const adEventEnum: any = 'adLoaded';

const Ads: UIComponent<{
  preload?: boolean;
  // eslint-disable-next-line no-unused-vars
  onStatusChange?: (state: statusChangeEventParam, element?: HTMLModElement | null) => any;
  size: [number, number];
  /**
   * base64 string of the html to render in case of empty
   * render handle by arkeero ads script
   */
  passback?: string;
}> = ({ className, onStatusChange, size, preload, passback, ...props }) => {
  const ref = useRef<HTMLModElement>(null);
  const { ads } = useAds();
  const [state, setState] = useState<statusChangeEventParam>({
    status: preload ? 'loading' : 'hidden',
  });

  const onViewport = useCallback((isOnViewport) => {
    setState((currentState) => {
      if (currentState.status === 'hidden' && isOnViewport) {
        return {
          status: 'loading',
        };
      }
      return currentState;
    });
  }, []);

  useOnViewport(ref, onViewport);

  useEffect(() => {
    onStatusChange?.(state, ref.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state]);

  useEffect(() => {
    const { current: ins } = ref;

    if (!ins || (state.status !== 'loading' && state.status !== 'empty')) {
      return undefined;
    }

    const listener = (evt: AdEvent) => {
      console.debug(adEventEnum, 'event fired', { element: ins, event: evt });
      setState({
        status: evt.detail.isEmpty ? 'empty' : 'loaded',
      });
    };
    ins.addEventListener(adEventEnum, listener, true);

    return () => {
      ins.removeEventListener(adEventEnum, listener, true);
    };
  }, [state]);

  useEffect(() => {
    if (state.status === 'hidden') {
      console.debug('Not on viewport, wait for it');
      return;
    }
    const adsScript = `${configuration.host}/ads/init.js`;
    if (!ref.current || ref.current.querySelector(`script[src="${adsScript}"]`)) {
      return;
    }
    const { current: ins } = ref;

    const script = document.createElement('script');
    script.src = adsScript;
    script.async = true;

    ins.appendChild(script);
  }, [state]);

  const [width, height] = size;
  return (
    <Element
      as="ins"
      {...props}
      className={clsx(className, styles.root)}
      ref={ref}
      data-size={`${width}x${height}`}
      data-ads={ads || '{}'}
      data-passback={passback}
    />
  );
};

export default Ads;
